import { Component, HostBinding, ViewChild } from '@angular/core';
import { createOrgNameOrIdRoleHandles, isArray } from '../../../../models/utils';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, OperationType, UpdateStatusType, SettingsType, AuthenticationMethod, UserType, OrganizationRole, SystemRole, Status, UsersAndGroupsMembershipActionType, AuthenticationMethodTPAuth } from '../../../../models/enums';
import { CellTemplateType, FilterTemplateType, GenericColDef } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { TpAuthStatus, User } from '../../../../models/user';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { ChangePasswordModalComponent } from '../../change-password-modal/change-password-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { NewUserModalComponent } from '../../new-user-modal/new-user-modal.component';
import { LogoutService } from '../../../../services/logout/logout.service';
import { RowNode } from 'ag-grid-community';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { UsersAndGroupsMembershipModalComponent } from '../../users-and-groups-membership-modal/users-and-groups-membership-modal.component';

@Component({
    standalone: false,
    selector: 'res-users-permissions',
    templateUrl: './users-permissions.component.html'
})
export class UsersPermissionComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;

    gridName = 'Users';
    automationId = 'users-permission';
    helpIndex: HelpIndex;
    handle: string;
    username: string;
    hasPermission: boolean;
    updateStatusType = UpdateStatusType.USER;

    isBusy = false;
    isBusyMessage = 'Loading Organization Users ...';
    currentHandle = '';
    isSystemSettingsContext: boolean;
    newUserBtn = 'New Organization User ...';

    isSysAdminOrITAdminUser = false;

    typeFilterItems: Array<string> = ['All', ...this.commonService.getEnumNames(UserType)];
    authenticationMethodFilterItems: Array<string> = ['All', ...this.commonService.getEnumNames(this.commonService?.isTPAuthEnabledSystem ? AuthenticationMethodTPAuth : AuthenticationMethod)];

    constructor(private commonService: CommonService,
        private restService: RestService, private logoutService: LogoutService) {
        let currentUserRole = commonService.currentUser.userRole;

        if (currentUserRole === 'IT Administrator' || currentUserRole === 'System Administrator') {
            this.isSysAdminOrITAdminUser = true;
        }
    }

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Type',
            field: 'type',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.nameIconLabel,
            minWidth: 120,
        },
        {
            headerName: 'Username',
            field: 'userName',
            automationId: this.automationId,
            minWidth: 100,
            suppressAutoSize: true,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: 'USERNAME',
        },
        {
            headerName: 'Role',
            field: 'userRole',
            automationId: this.automationId,
            minWidth: 176, maxWidth: 176, width: 176,
            filterType: FilterTemplateType.singleChoice,
            serviceTierFilter: true,
            filterAttributeName: 'ROLETYPE',
            filterItemSelected: 'All',
        },
        {
            headerName: 'First Name',
            field: 'firstName',
            automationId: this.automationId,
            minWidth: 100,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: 'FIRST_NAME',
        },
        {
            headerName: 'Last Name',
            field: 'lastName',
            automationId: this.automationId,
            minWidth: 100,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: 'LAST_NAME',
        },
        {
            headerName: 'Email',
            field: 'email',
            automationId: this.automationId,
            minWidth: 100,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: 'EMAIL',
        },
        {
            headerName: 'Authentication Method',
            field: 'authenticationMethod',
            automationId: this.automationId,
            minWidth: 156, maxWidth: 156, width: 156,
            cellTemplateType: CellTemplateType.userAuthenticationMethod,
            filterType: FilterTemplateType.singleChoice,
            serviceTierFilter: true,
            filterAttributeName: 'AUTHENTICATION_METHOD',
            filterItems: this.authenticationMethodFilterItems,
            filterItemSelected: 'All',
        },
        {
            headerName: 'TP Auth Status',
            field: 'tpAuthUIUserStatus',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.tpAuthUserStatusIconOrLabel,
            filterType: FilterTemplateType.singleChoice,
            width: 100, minWidth: 100,
            filterItems: ['All', TpAuthStatus.Success, TpAuthStatus.Failure],
            filterItemSelected: 'All',
            filterAttributeName: 'TPAuth_Status',
            serviceTierFilter: true
        },
        // non TP Auth systems:
        {
            headerName: 'Status',
            field: 'userStatus',
            automationId: this.automationId,
            lockVisible: true,
            width: 100, minWidth: 100,
            cellTemplateType: CellTemplateType.userStatus,
            filterType: FilterTemplateType.singleChoice,
            serviceTierFilter: true,
            filterAttributeName: 'STATUS',
            filterItems: ['All', Status.NOT_DISABLED_LOCKED, Status.LOCKED, Status.DISABLED],
            filterItemSelected: 'All'
        },
        // TP Auth systems:
        {
            headerName: 'Status',
            field: 'userStatus',
            automationId: this.automationId,
            lockVisible: true,
            width: 100, minWidth: 100,
            cellTemplateType: CellTemplateType.tpAuthUserStatus,
            filterType: FilterTemplateType.singleChoice,
            serviceTierFilter: true,
            filterAttributeName: 'STATUS',
            filterItems: ['All', Status.DISABLED, Status.NOT_DISABLED],
            filterItemSelected: 'All'
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId,
            minWidth: 100,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: 'DESCRIPTION',
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true,

        },
    );

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.orgManager_listUsers,
        responseName: 'users',
        data: { organizationName: '' }
    });

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Change Password ...', iconClass: 'fa-key', iconColor: 'color-yellow-07', automationId: 'org-settings-users-changePassword-user',
            click: (item) => this.changePassword(item), visible: (item) => !item.isTpAuth,
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({
            name: 'Unlock', iconClass: 'fa-unlock', iconColor: 'color-red-05', automationId: 'org-settings-users-unlock-user',
            click: (item) => this.unlockUser(item), enabled: (item) => item.locked, visible: (item) => !item.isTpAuth,
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),

        new ContextMenuOption({
            name: 'Enable', iconClass: 'fa-pause-circle', iconColor: 'color-grey-08', automationId: 'org-settings-users-enable-user',
            click: (item) => this.enableDisableUser(item), enabled: (item) => this.canLoggedInUserDisable(item), visible: (item) => !item.enabled && this.isSysAdminOrITAdminUser,
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({
            name: 'Disable', iconClass: 'fa-play-circle', iconColor: 'color-grey-08', automationId: 'org-settings-users-disable-user',
            click: (item) => this.enableDisableUser(item), enabled: (item) => this.canLoggedInUserEnable(item), visible: (item) => item.enabled && this.isSysAdminOrITAdminUser,
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),

        new ContextMenuOption({
            name: 'Retry TP Auth Setup', iconClass: 'fa-repeat', iconColor: 'color-yellow-07', automationId: 'org-settings-users-retry-tp-auth-setup',
            click: (item) => this.retryTpAuthSetup(item),
            visible: (item) => { if (item) { return item.isTpAuth && item.tpAuthUIUserStatus === TpAuthStatus.Failure; } else return false; },
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({
            name: 'Log out on All Devices', iconClass: 'fa-sign-out', iconColor: 'color-grey-10', automationId: 'org-settings-users-log-out-all-devices-user',
            click: (item) => this.logOutUser(item),
            visible: (item) => { return !item.isTpAuth; },
            tooltip: 'The user will be logged out on all devices and the "Remember Me" password cleared.', tooltipAlwaysOn: true,
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({ divider: true, visible: (item) => !item.isTpAuth || item.isTpAuth && item.tpAuthUIUserStatus === TpAuthStatus.Failure }),
        new ContextMenuOption({
            name: 'Add to / Remove from Projects ...',
            iconClass: 'fa-book', iconColor: 'color-grey-08', iconSecondClass: 'fa-pencil', iconSecondColor: 'color-blue-01',
            automationId: 'organization-settings-users-add-to-remove-from-projects',
            click: (item) => this.addToRemoveFromProjsOrOrgs(item, UsersAndGroupsMembershipActionType.addToRemoveFromProjects),
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true,
            visible: (item: GenericTreeNode) => !this.isSystemSettingsContext, secondIconCustomClass: true
        }),
        new ContextMenuOption({
            name: 'Add to / Remove from Organizations ...',
            iconClass: 'fa-bank', iconColor: 'color-grey-08', iconSecondClass: 'fa-pencil', iconSecondColor: 'color-blue-01',
            automationId: 'system-settings-users-add-to-remove-from-organizations',
            click: (item) => this.addToRemoveFromProjsOrOrgs(item, UsersAndGroupsMembershipActionType.addToRemoveFromOrganizations),
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true,
            visible: (item: GenericTreeNode) => this.isSystemSettingsContext, secondIconCustomClass: true
        }),
        new ContextMenuOption({ divider: true, visible: (item) => !item.isTpAuth }),
        new ContextMenuOption({
            name: 'Edit...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: 'org-settings-users-edit-user',
            click: (item) => this.editUser(item),
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: 'org-settings-users-delete-user',
            click: (item) => this.deleteUser(item),
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.DELETE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.DELETE] }],
            atLeastOne: true, enabled: (item) => this?.isSystemSettingsContext || item?.type !== 'System User',
            visible: (item) => this.isLastSystemAdmin(item) && this.hasDeletePermission()
        })
    ];

    rowClassRules = {
        'disabled-row': (params: any): boolean => {
            return false;
        },
        'disabled-cells': (params: any): boolean => {
            return ((params.data instanceof User) && (params.data as User).enabled == false);
        }
    };

    retryTpAuthSetup(item: User): void {
        if (item) {
            this.isBusy = true;
            this.isBusyMessage = 'Retry TP Auth Setup ...';
            if (item.tpAuthUserStatus === TpAuthStatus.Partial) {
                this.commonService.openModal(NewUserModalComponent,
                    { backdrop: false, modalData: { operationType: OperationType.edit, userItem: item, isSystemUser: this.isSystemSettingsContext, gridUserList: this.genericGrid.getData(), isRetryTPAuth: true } }).then((response) => {
                        this.isBusy = false;
                        if (response?.modalData?.password) {
                            this.createUserInTPAuth(item, response.modalData.password);
                        }
                    });
            } else {
                this.createUserInTPAuth(item);
            }

        }
    }

    createUserInTPAuth(item: User, password: string = null) {
        let request: any = {
            domainHandle: item.domainHandle,
            local: true,
            roleHandles: item.roleHandles,
            password: password,
            userName: item.userName,
            email: item.email,
            description: item.description,
            firstName: item.firstName,
            lastName: item.lastName,
            mfa: item.mfa,
            conditionalOnIPAddress: item.conditionalOnIPAddress,
            allowedIPAddressRange: item.allowedIPAddressRange,
            systemScope: this.isSystemSettingsContext,
            orgName: item.customerName
        };
        if (this.isSystemSettingsContext) {
            request.orgName = 'super_system_customer';
        }
        this.restService.post(Endpoint.userManager_createUserInTPAuth, request, false).then(response => {
            this.isBusy = false;
            if (response) {
                this.loadDataWithFilters();
            }
        });
    }

    changePassword(item: User): void {
        this.commonService.openModal(ChangePasswordModalComponent, {
            backdrop: false, class: 'horizontal-px-500',
            modalData: { entityType: 'change-password-no-current', userName: item.userName, orgHandle: item.customerName, isSystemUser: this.isSystemSettingsContext }
        }).then((result) => {
            if (result.modalResult) {
                this.loadDataWithFilters();
            }
        });
    }

    addNewUser(): void {
        this.commonService.openModal(NewUserModalComponent,
            { backdrop: false, modalData: { operationType: OperationType.new, isSystemUser: this.isSystemSettingsContext, gridUserList: this.genericGrid.getData() } }).then((result) => {
                if (result.modalResult) {
                    this.loadDataWithFilters();
                }
            });
    }

    addToRemoveFromProjsOrOrgs(item, action: UsersAndGroupsMembershipActionType): void {
        this.commonService.openModal(UsersAndGroupsMembershipModalComponent, {
            class: action === UsersAndGroupsMembershipActionType.addToRemoveFromProjects
                ? 'vertical-percent-80 horizontal-px-1200'
                : 'vertical-percent-80 horizontal-px-1000',
            modalData: {
                isMembershipModalOpenByUser: true,
                isMembershipModalOpenByAction: action,
                isSystemContext: this.isSystemSettingsContext,
                userName: item
            }
        });
    }

    editUser(item: User): void {
        this.commonService.openModal(NewUserModalComponent,
            { backdrop: false, modalData: { operationType: OperationType.edit, userItem: item, isSystemUser: this.isSystemSettingsContext, gridUserList: this.genericGrid.getData() } }).then((result) => {
                if (result.modalResult) {
                    this.loadDataWithFilters();
                }
            });
    }

    isLastSystemAdmin(item): boolean {
        if (item.userRole === 'System Administrator' && this.isSystemSettingsContext) {
            const systemAdminsList = this.genericGrid.getData().filter(role => role.userRole === 'System Administrator');
            return systemAdminsList.length > 1;
        }
        return true;
    }

    deleteUser(item: User): void {
        if (item) {
            this.genericGrid.gridContext.loadAndFilter = true;
            item.isSuperSystemUser = this.isSystemSettingsContext;
            Object.assign(item, { title: this.isSystemSettingsContext ? 'Delete System User' : 'Delete Organization User' });
            this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
        }
    }

    hasDeletePermission(): boolean {
        let hasDeletePermission = Permissions.checkUserPermission(ObjectActionListType.USERS, ActionType.DELETE);
        if (this.isSystemSettingsContext) {
            hasDeletePermission = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_USER, ActionType.DELETE);
        }
        return hasDeletePermission;
    }

    responseCallback = (response: any): void => {
        if (response && isArray(response.users)) {
            response.users = (response.users as Array<any>).map(u => new User(u));
        }
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    isDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy) {
            if (this.genericGrid?.filters?.length === 0) {
                isDisabled = false;
            }
        }
        return isDisabled;
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.isSystemSettingsContext = settingsType === SettingsType.system;
        const roleColDef = this.columnDefs.find(column => column.field === 'userRole');
        if (this.isSystemSettingsContext) {
            this.requestInfo = new DrRequestInfo({
                endpoint: Endpoint.adminOrgManager_listUsersAndGroups,
                responseName: 'users',
                data: { organizationName: 'super_system_customer', onlyGroups: false, onlyUsers: true, systemScope: this.isSystemSettingsContext }
            });
            this.columnDefs[0].hide = this.isSystemSettingsContext;
            this.newUserBtn = 'New System User ...';
            this.isBusyMessage = 'Loading System Users ...';
            this.restService.post(Endpoint.realmManager_listSystemRoles, { objectType: 'ALL', systemScope: true }).then(response => {
                if (response?.roles && roleColDef) {
                    const roleNames = response.roles.map(role => role.name);
                    roleColDef.filterItems = ['All', ...roleNames];
                    if (this.genericGrid) {
                        this.genericGrid.setColumnDefs(this.columnDefs);
                        this.genericGrid.refreshColumns();
                    }
                }
            });
        }
        else {
            if (this.requestInfo?.data && this.commonService.currentUser) {
                this.requestInfo.data.organizationName = this.commonService.currentUser.customerName;
            }
            const typeColDef = this.columnDefs.find(column => column.field === 'type');
            if (typeColDef) {
                typeColDef.filterType = FilterTemplateType.singleChoice;
                typeColDef.serviceTierFilter = true;
                typeColDef.filterAttributeName = 'TYPE';
                typeColDef.filterItems = this.typeFilterItems;
                typeColDef.filterItemSelected = 'All';
            }
            roleColDef.filterItems = ['All', ...this.commonService.getEnumNames(OrganizationRole)];
        }
        this.helpIndex = this.isSystemSettingsContext ? HelpIndex.system_users_manage_dlg : HelpIndex.users_add_manage_org_dlg;
        this.hasPermission = Permissions.checkUserPermission(ObjectActionListType.USERS, ActionType.CREATE);
        if (this.isSystemSettingsContext) {
            this.hasPermission = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_USER, ActionType.CREATE);
        }
        if (this.commonService?.isTPAuthEnabledSystem) {
            this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.userStatus);
        } else {
            this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.tpAuthUserStatusIconOrLabel);
            this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.tpAuthUserStatus);
        }
        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasPermission) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.contextMenuOptions = null;
            }
        }

        this.genericGrid.loadData();
    }

    unlockUser(item: User): void {
        this.restService.post(Endpoint.orgManager_unlockUser, { handle: item.handle, systemScope: this.isSystemSettingsContext }).then(response => {
            this.isBusy = false;
            if (response) {
                this.loadDataWithFilters();
            }
        });
    }

    enableDisableUser(item: User): void {
        this.restService.post(Endpoint.orgManager_enableDisableUser, { handle: item.handle, systemScope: this.isSystemSettingsContext, locked: item.locked }).then(response => {
            this.isBusy = false;
            if (response) {
                this.loadDataWithFilters();
            }
        });
    }

    canLoggedInUserEnable(item: User): boolean {
        let currentUser = this.commonService.currentUser;
        let userHasRightToEnable = false;

        if (!this.isSystemSettingsContext && item.type === 'System User') {
            return false;
        }

        if (currentUser.userRole === 'IT Administrator' || currentUser.userRole === 'System Administrator') {
            userHasRightToEnable = true;

            if ((currentUser.userRole === 'System Administrator' && item.userRole === 'IT Administrator') || (item.userName === currentUser.userName)) {
                userHasRightToEnable = false;
            }
        }

        return item.enabled && userHasRightToEnable;
    }

    canLoggedInUserDisable(item: User): boolean {
        let currentUser = this.commonService.currentUser;
        let userHasRightToDisable = false;

        if (!this.isSystemSettingsContext && item.type === 'System User') {
            return false;
        }

        if (currentUser.userRole === 'IT Administrator' || currentUser.userRole === 'System Administrator') {
            userHasRightToDisable = true;

            if ((currentUser.userRole === 'System Administrator' && item.userRole === 'IT Administrator') || (item.userName === currentUser.userName)) {
                userHasRightToDisable = false;
            }
        }

        return !item.enabled && userHasRightToDisable;
    }

    loadDataWithFilters(): void {
        this.genericGrid.gridContext.loadAndFilter = true;
        this.genericGrid.loadData();
        this.genericGrid.gridContext.loadAndFilter = false;
    }

    logOutUser(item: User): void {
        if (item) {
            this.logoutService.logoutOnAllDevices(item);
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode): void => {
        if (rowNode?.data && this.hasPermission) {
            const selectedUser = new User(rowNode.data);
            if (selectedUser) {
                this.editUser(selectedUser);
            }
        }
    }
}
